import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import { ApiResponse } from '../shared/types';
import type {
  AddMembersData,
  AddMembersPayload,
  ExistingMemberCandidate,
  FamilyAddContext,
  FamilyMovePayload,
  FamilyMovePreview,
  FamilyMoveResult,
  FamilyOtpSent,
  RemoveMemberPayload,
} from '../shared/types/family-members.types';

/** Adding members to a started family (CWS-1581). */
@Injectable({ providedIn: 'root' })
export class FamilyMembersService {
  constructor(private readonly api: ApiService) {}

  context(groupId: string, lprEntryId?: string): Observable<ApiResponse<FamilyAddContext>> {
    return this.api.get<FamilyAddContext>(
      `order/family-plan/groups/${groupId}/add-members/context`,
      lprEntryId ? { lpr_entry_id: lprEntryId } : {}
    );
  }

  searchExisting(
    groupId: string,
    query: string
  ): Observable<ApiResponse<ExistingMemberCandidate[]>> {
    return this.api.get<ExistingMemberCandidate[]>(
      `order/family-plan/groups/${groupId}/existing-members`,
      { q: query }
    );
  }

  sendOtp(
    groupId: string,
    action: 'add_plate' | 'add_member'
  ): Observable<ApiResponse<FamilyOtpSent>> {
    return this.api.post<FamilyOtpSent>(`order/family-plan/groups/${groupId}/otp`, { action });
  }

  addMembers(groupId: string, payload: AddMembersPayload): Observable<ApiResponse<AddMembersData>> {
    return this.api.post<AddMembersData>(`order/family-plan/groups/${groupId}/members`, payload);
  }

  /** Takes a plate out of the family (CWS-1815). The card cases need more than this sends. */
  removeMember(
    groupId: string,
    memberId: string,
    payload: RemoveMemberPayload
  ): Observable<ApiResponse<unknown>> {
    return this.api.post<unknown>(
      `order/family-plan/groups/${groupId}/members/${memberId}/remove`,
      payload
    );
  }

  /** CWS-1579: whether the number already has a customer, and the cards they have saved. */
  movePreview(
    groupId: string,
    memberId: string,
    phone: string
  ): Observable<ApiResponse<FamilyMovePreview>> {
    return this.api.post<FamilyMovePreview>(
      `order/family-plan/groups/${groupId}/members/${memberId}/move-preview`,
      { phone }
    );
  }

  /** Gives the plate a customer of its own (CWS-1582 §7). */
  moveMember(
    groupId: string,
    memberId: string,
    payload: FamilyMovePayload
  ): Observable<ApiResponse<FamilyMoveResult>> {
    return this.api.post<FamilyMoveResult>(
      `order/family-plan/groups/${groupId}/members/${memberId}/move`,
      payload
    );
  }
}
